import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react'
import { useLocation, useNavigate, useNavigationType, type Location } from 'react-router-dom'

import { MOTION_MS, useReducedMotion } from './motion'
import {
  BACK_DECIDE_PX,
  BACK_EDGE_PX,
  backProgress,
  decideSwipeAxis,
  BACK_STATE,
  initialStage,
  isBackState,
  layerAnim,
  locKey,
  nextStage,
  shouldCompleteBack,
  underAt,
  velocityOf,
  type Entry,
  type LayerRole,
  type Loc,
  type Stage,
} from './nav'
import { subscribeTabReselect } from './reselect'

// ============================================================================
// THE NAV LAYER — board 25 (Transitions.dc.html) and the motion spec in
// docs/design/mobile-redesign-handoff.md.
//
//   · TAB SWITCH, fade through, never overlapping: the old page fades out and lifts 6px (90ms),
//     then the new one fades in (160ms, 90ms delay) rising from 8px below.
//   · PUSH: the detail slides in from the right (340ms `cubic-bezier(.32,.72,0,1)`) with a soft
//     left shadow; the page under drifts to −28% and darkens to .82; the tab bar slides away.
//   · BACK: the reverse — and an edge-swipe from the left drags it live.
//   · Reduced motion: every one of them is a 160ms opacity fade.
//
// HOW: each mounted page is a LAYER keyed by a stable id (mobile/shell/nav.ts → `Entry`). A page
// keeps its id while its role changes — current → under on a push, under → current on a back —
// so the School list you pushed a course from is the same mounted tree, at the same scroll
// position, when you come back to it. Layers are rendered in id order, never re-sorted, so React
// never moves a page's DOM (which would reset its scroll); z-order comes from CSS by role.
//
// WHAT IS A PUSH is decided in nav.ts (`transitionKind`, `nextStage`) and pinned by tests.
//
// THE EDGE SWIPE WRITES STYLES, NOT STATE: a press within 24px of the left edge of a pushed
// screen, decided after 8px of movement (vertical wins, so a scroll is never stolen), moves the
// detail with `style.transform` on its element and the page under and the bar in proportion. React
// hears about it once: on release, when the back completes and the router moves.
// ============================================================================

export function NavStage({
  children,
  bar,
  barRef,
}: {
  /** Renders the routes for one layer's location. */
  children: (location: Location) => ReactNode
  /** The tab bar, rendered inside the stage so it stacks with the pages. Told when it should hide. */
  bar: (pushed: boolean) => ReactNode
  /** The bar's outer element, which the edge-swipe slides back in step with the finger. */
  barRef: RefObject<HTMLDivElement | null>
}) {
  const location = useLocation()
  const navigationType = useNavigationType()
  const navigate = useNavigate()
  const reduced = useReducedMotion()

  const ids = useRef(0)
  const newId = () => ++ids.current
  const asLoc = (l: Loc): Location => ({ ...l, state: null, key: `root:${l.pathname}` })

  const [stage, setStage] = useState<Stage<Location>>(() => initialStage(location, newId, asLoc))
  const stageRef = useRef(stage)
  stageRef.current = stage

  const swiped = useRef(false)
  const layers = useRef(new Map<number, HTMLDivElement>())
  const stageEl = useRef<HTMLDivElement>(null)

  // The router moved: work out the next stage BEFORE paint, so no frame shows a page in the wrong role.
  useLayoutEffect(() => {
    const prev = stageRef.current
    if (prev.current.location === location) return
    // A back-by-replace (a cold-opened detail's back control, mobile/shell/parts.tsx → BackRow)
    // is still a back, and animates as one.
    const type = navigationType === 'REPLACE' && isBackState(location.state) ? 'POP' : navigationType
    const next = nextStage(prev, location, type, {
      swiped: swiped.current,
      reduced,
      newId,
      asLoc,
    })
    swiped.current = false
    if (next !== prev) setStage(next)
    // Only a navigation drives this; `reduced` is read at the moment it happens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location])

  // A page that just changed role keeps no leftover inline styles from a drag.
  useLayoutEffect(() => {
    for (const el of layers.current.values()) clearDragStyles(el)
    if (barRef.current) clearDragStyles(barRef.current)
  }, [stage.current.id, barRef])

  // The leaving page goes when its animation has finished.
  useEffect(() => {
    if (stage.anim === null) return
    const ms = reduced ? MOTION_MS.fade : stage.anim === 'tab' ? MOTION_MS.tab : MOTION_MS.push
    const timer = window.setTimeout(() => {
      setStage((s) => (s.anim === null ? s : { ...s, leaving: null, anim: null }))
    }, ms)
    return () => window.clearTimeout(timer)
  }, [stage.anim, stage.current.id, reduced])

  // Re-tapping the active tab: every scrolled region of the visible page back to the top.
  useEffect(
    () =>
      subscribeTabReselect(() => {
        const el = layers.current.get(stageRef.current.current.id)
        if (!el) return
        for (const node of [el, ...el.querySelectorAll<HTMLElement>('*')]) {
          if (node.scrollTop <= 0) continue
          if (typeof node.scrollTo === 'function') {
            node.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' })
          } else {
            node.scrollTop = 0
          }
        }
      }),
    [reduced],
  )

  // ── The live edge-swipe back ──────────────────────────────────────────────────────────────
  const goBack = useRef<() => void>(() => {})
  goBack.current = () => {
    const under = stageRef.current.under
    if (!under) return
    swiped.current = true
    if (under.fromHistory) navigate(-1)
    else navigate(locKey(under.location), { replace: true, state: BACK_STATE })
  }

  useEffect(() => {
    const root = stageEl.current
    if (!root) return

    interface Drag {
      pointerId: number
      x0: number
      y0: number
      decided: boolean
      dx: number
      width: number
      scale: number
      detail: HTMLDivElement
      /** Looked up on each frame: a deep link's root page is only mounted once the swipe starts. */
      underId: number
      under: HTMLDivElement | null
      bar: HTMLDivElement | null
      barHeight: number
      samples: { x: number; t: number }[]
      frame: number
    }
    let drag: Drag | null = null
    let suppressClick = false

    const findUnder = (d: Drag) => {
      if (d.under) return d.under
      const el = layers.current.get(d.underId) ?? null
      if (el) {
        el.style.transition = 'none'
        el.style.visibility = 'visible'
        d.under = el
      }
      return el
    }

    const paint = (d: Drag) => {
      d.frame = 0
      const p = backProgress(d.dx, d.width)
      const u = underAt(p)
      d.detail.style.transform = `translateX(${d.dx / d.scale}px)`
      const under = findUnder(d)
      if (under) {
        under.style.transform = `translateX(${u.translatePercent}%)`
        under.style.filter = `brightness(${u.brightness})`
      }
      if (d.bar) d.bar.style.transform = `translateY(${(1 - p) * d.barHeight}px)`
    }

    const onDown = (e: PointerEvent) => {
      const s = stageRef.current
      if (drag || e.button > 0 || !s.current.pushed || !s.under || s.anim !== null) return
      const detail = layers.current.get(s.current.id)
      if (!detail) return
      const r = detail.getBoundingClientRect()
      const scale = detail.offsetWidth > 0 && r.width > 0 ? r.width / detail.offsetWidth : 1
      if (e.clientX - r.left > BACK_EDGE_PX * scale) return
      // The bar comes back with the finger only when the page underneath is a tab's own screen.
      const bar = s.under.pushed ? null : barRef.current
      drag = {
        pointerId: e.pointerId,
        x0: e.clientX,
        y0: e.clientY,
        decided: false,
        dx: 0,
        width: r.width,
        scale,
        detail,
        underId: s.under.id,
        under: null,
        bar,
        barHeight: bar ? hiddenBarOffset(bar) : 0,
        samples: [{ x: e.clientX, t: e.timeStamp }],
        frame: 0,
      }
    }

    const onMove = (e: PointerEvent) => {
      const d = drag
      if (!d || e.pointerId !== d.pointerId) return
      const dx = e.clientX - d.x0
      const dy = e.clientY - d.y0
      if (!d.decided) {
        const axis = decideSwipeAxis(dx, dy, BACK_DECIDE_PX * d.scale)
        if (axis === 'pending') return
        if (axis === 'cancel') {
          drag = null
          return
        }
        d.decided = true
        for (const el of [d.detail, d.bar]) if (el) el.style.transition = 'none'
        // The one React update a swipe makes before release: mount the page it will uncover, if
        // it is a deep link's root that was never rendered.
        if (stageRef.current.under && !stageRef.current.under.live) {
          setStage((st) => (st.under && !st.under.live ? { ...st, under: { ...st.under, live: true } } : st))
        }
        findUnder(d)
      }
      d.dx = Math.max(0, dx)
      d.samples.push({ x: e.clientX, t: e.timeStamp })
      if (d.samples.length > 12) d.samples.shift()
      if (!d.frame) d.frame = requestAnimationFrame(() => paint(d))
    }

    const settle = (d: Drag, complete: boolean) => {
      if (d.frame) cancelAnimationFrame(d.frame)
      const curve = 'var(--m-dur-push) var(--m-ease-push)'
      const under = findUnder(d)
      d.detail.style.transition = `transform ${curve}`
      if (under) under.style.transition = `transform ${curve}, filter ${curve}`
      if (d.bar) d.bar.style.transition = `transform ${curve}`
      if (complete) {
        d.detail.style.transform = 'translateX(100%)'
        if (under) {
          under.style.transform = 'translateX(0)'
          under.style.filter = 'brightness(1)'
        }
        if (d.bar) d.bar.style.transform = 'translateY(0)'
        window.setTimeout(() => goBack.current(), MOTION_MS.push)
      } else {
        const u = underAt(0)
        d.detail.style.transform = 'translateX(0)'
        if (under) {
          under.style.transform = `translateX(${u.translatePercent}%)`
          under.style.filter = `brightness(${u.brightness})`
        }
        if (d.bar) d.bar.style.transform = `translateY(${d.barHeight}px)`
        window.setTimeout(() => {
          for (const el of [d.detail, under, d.bar]) if (el) clearDragStyles(el)
        }, MOTION_MS.push)
      }
    }

    const onUp = (e: PointerEvent) => {
      const d = drag
      if (!d || e.pointerId !== d.pointerId) return
      drag = null
      if (!d.decided) return
      d.samples.push({ x: e.clientX, t: e.timeStamp })
      // A finger that dragged a page should not also tap whatever it was released over.
      suppressClick = true
      window.setTimeout(() => {
        suppressClick = false
      }, 0)
      settle(d, shouldCompleteBack(d.dx, d.width, velocityOf(d.samples)))
    }

    const onCancel = (e: PointerEvent) => {
      const d = drag
      if (!d || e.pointerId !== d.pointerId) return
      drag = null
      if (d.decided) settle(d, false)
    }

    // Once the swipe is ours, the browser must not also scroll: a non-passive touchmove can say so.
    const onTouchMove = (e: TouchEvent) => {
      if (drag?.decided && e.cancelable) e.preventDefault()
    }
    const onClick = (e: MouseEvent) => {
      if (!suppressClick) return
      e.stopPropagation()
      e.preventDefault()
    }

    root.addEventListener('pointerdown', onDown, true)
    root.addEventListener('touchmove', onTouchMove, { passive: false })
    root.addEventListener('click', onClick, true)
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onCancel)
    return () => {
      root.removeEventListener('pointerdown', onDown, true)
      root.removeEventListener('touchmove', onTouchMove)
      root.removeEventListener('click', onClick, true)
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onCancel)
    }
  }, [barRef])

  const roles: [Entry<Location>, LayerRole][] = []
  if (stage.under?.live) roles.push([stage.under, 'under'])
  roles.push([stage.current, 'current'])
  if (stage.leaving) roles.push([stage.leaving, 'leaving'])
  roles.sort((a, b) => a[0].id - b[0].id)

  return (
    <div
      ref={stageEl}
      className="m-stage"
      data-shell-stage=""
      // THE "TRANSITION SETTLED" SIGNAL: no animation running and no page on its way out, so the
      // only page mounted in front is the current one (a leaving page is unmounted — and its
      // unmount effects, like the journal composer's flush, have run). Tests wait on this rather
      // than on a guessed delay; see mobile/shell/settled.ts.
      data-settled={stage.anim === null && stage.leaving === null ? 'true' : 'false'}
    >
      {roles.map(([entry, role]) => (
        <div
          key={entry.id}
          ref={(el) => {
            if (el) layers.current.set(entry.id, el)
            else layers.current.delete(entry.id)
          }}
          className="m-layer bg-page"
          data-role={role}
          data-anim={layerAnim(role, stage.anim, reduced) ?? undefined}
          data-pushed={entry.pushed ? 'true' : undefined}
          aria-hidden={role !== 'current' || undefined}
          inert={role !== 'current'}
        >
          {children(entry.location)}
        </div>
      ))}
      {bar(stage.current.pushed)}
    </div>
  )
}

/**
 * How far down the bar sits while it is away — read off its own computed transform (the class's
 * `translateY(100% + gap + 40px)`), in the bar's own pixels. The bar counter-zooms the app's size
 * setting, so layout offsets measured against the zoomed page would be in the wrong units; its own
 * transform never is.
 */
function hiddenBarOffset(bar: HTMLElement): number {
  try {
    const ty = new DOMMatrixReadOnly(getComputedStyle(bar).transform).m42
    if (ty > 0) return ty
  } catch {
    // `none`, or no DOMMatrix: fall through to a distance that is certainly off screen.
  }
  return bar.offsetHeight + 160
}

function clearDragStyles(el: HTMLElement) {
  el.style.transform = ''
  el.style.filter = ''
  el.style.transition = ''
  el.style.visibility = ''
}
